import { Editor } from "@tldraw/tldraw";

const median = (values: number[]) => {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
};

/**
 * Re-layouts the given shapes into a clean grid:
 *   - Groups shapes into rows based on their vertical center proximity
 *     (median-height tolerance so rows tolerate slight Y misalignment).
 *   - Sorts each row left-to-right by horizontal center.
 *   - Snaps positions onto a uniform grid using the median of the existing
 *     X / Y gaps as the new gap (with an 8px floor so collapsed layouts
 *     still get sensible breathing room).
 *
 * Pure side-effect on `editor.updateShape`. No-op for fewer than 2 shapes
 * (no meaningful tidy can be applied) or when bounds aren't resolvable for
 * at least 2 shapes (very edge — shape just got deleted, custom shape with
 * no bounds, etc.).
 *
 * Lifted out of RotateComponent so callers without that component mounted
 * (e.g. the global Cmd/Ctrl+Alt+T shortcut handler in BottomToolPanel) can
 * invoke the same logic without duplicating ~80 lines.
 */
export const runTidyUp = (editor: Editor, shapes: any[]): void => {
  if (!shapes || shapes.length < 2) return;

  const shapesWithBounds = shapes
    .filter((shape) => shape && shape.id)
    .map((shape) => {
      const bounds = editor.getShapePageBounds(shape);
      return { shape, bounds };
    })
    .filter((item) => Boolean(item.bounds));

  if (shapesWithBounds.length < 2) return;

  const minX = Math.min(...shapesWithBounds.map(({ bounds }: any) => bounds.minX));
  const minY = Math.min(...shapesWithBounds.map(({ bounds }: any) => bounds.minY));

  const heights = shapesWithBounds.map(({ bounds }: any) => bounds.height).filter((h: number) => h > 0);
  const medianHeight = median(heights) || 1;
  const rowThreshold = Math.max(6, medianHeight * 0.6);

  const sortedForRows = [...shapesWithBounds].sort((a: any, b: any) => a.bounds.center.y - b.bounds.center.y);

  const rows: any[][] = [];
  sortedForRows.forEach((item: any) => {
    const lastRow = rows[rows.length - 1];
    if (!lastRow) {
      rows.push([item]);
      return;
    }
    const rowCenterY = median(lastRow.map((r: any) => r.bounds.center.y));
    if (Math.abs(item.bounds.center.y - rowCenterY) <= rowThreshold) {
      lastRow.push(item);
    } else {
      rows.push([item]);
    }
  });

  rows.forEach((row) => row.sort((a: any, b: any) => a.bounds.center.x - b.bounds.center.x));

  const gapsX: number[] = [];
  rows.forEach((row) => {
    for (let i = 1; i < row.length; i++) {
      const prev = row[i - 1].bounds;
      const curr = row[i].bounds;
      const gap = curr.minX - prev.maxX;
      if (gap > 0) gapsX.push(gap);
    }
  });

  const gapsY: number[] = [];
  for (let i = 1; i < rows.length; i++) {
    const prevRow = rows[i - 1];
    const currRow = rows[i];
    const prevMaxY = Math.max(...prevRow.map((r: any) => r.bounds.maxY));
    const currMinY = Math.min(...currRow.map((r: any) => r.bounds.minY));
    const gap = currMinY - prevMaxY;
    if (gap > 0) gapsY.push(gap);
  }

  const gapX = Math.max(8, median(gapsX) || 16);
  const gapY = Math.max(8, median(gapsY) || 16);

  let cursorY = minY;
  rows.forEach((row) => {
    let cursorX = minX;
    const rowHeight = Math.max(...row.map((r: any) => r.bounds.height));
    row.forEach((item: any) => {
      const w = item.bounds.width;
      const h = item.bounds.height;
      editor.updateShape({
        ...item.shape,
        x: cursorX,
        y: cursorY,
        props: {
          ...item.shape.props,
          w: item.shape.props?.w ?? w,
          h: item.shape.props?.h ?? h,
        },
      });
      cursorX += w + gapX;
    });
    cursorY += rowHeight + gapY;
  });
};
